
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Modal,
    Form,
    Input,
    Select,
    Button,
    Row,
    Col,
    message,
    Upload,
    Spin,
    Avatar,
    Tag,
    Space,
    Divider,
    Alert,
} from "antd";

import {
    UploadOutlined,
    UserOutlined,
    CloseOutlined,
    SaveOutlined,
    BookOutlined,
    IdcardOutlined,
    TeamOutlined,
    CalendarOutlined,
    HeartOutlined,
    ReloadOutlined,
    CameraOutlined,
} from "@ant-design/icons";

import api from "../../../api/api";

const { Option } = Select;

const NAVY = "#062A4B";
const BLUE = "#063A6B";
const GOLD = "#F6C944";

const getResults = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.data)) return data.data;
    return [];
};

const getRelatedId = (value) => {
    if (value === null || value === undefined || value === "") {
        return undefined;
    }

    if (typeof value === "object") {
        return value.id ?? value.pk ?? undefined;
    }

    return value;
};

const getErrorMessage = (error) => {
    const data = error?.response?.data;

    if (data && typeof data === "object") {
        return Object.entries(data)
            .map(([field, errors]) => {
                const text = Array.isArray(errors)
                    ? errors.map((item) =>
                        typeof item === "string"
                            ? item
                            : item?.message || JSON.stringify(item)
                    ).join(", ")
                    : typeof errors === "string"
                        ? errors
                        : JSON.stringify(errors);

                return `${field}: ${text}`;
            })
            .join(" | ");
    }

    return error?.message || "Unable to update student admission.";
};

const EditStudentAdmissionPopup = ({
    visible = false,
    student = null,
    onClose,
    refresh,
}) => {
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [loadingRecord, setLoadingRecord] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [optionError, setOptionError] = useState("");

    const [academicYears, setAcademicYears] = useState([]);
    const [grades, setGrades] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [superParents, setSuperParents] = useState([]);

    const [imageFileList, setImageFileList] = useState([]);
    const [imageUrl, setImageUrl] = useState("");
    const [record, setRecord] = useState(null);

    const studentRef = useRef(student);
    const requestSequenceRef = useRef(0);
    const savingRef = useRef(false);
    const visibleRef = useRef(visible);
    const imageUrlRef = useRef("");

    const studentId = student?.id;

    useEffect(() => {
        studentRef.current = student;
    }, [student]);

    useEffect(() => {
        visibleRef.current = visible;
    }, [visible]);

    useEffect(() => {
        imageUrlRef.current = imageUrl;
    }, [imageUrl]);

    const config = useMemo(() => ({
        headers: {
            Authorization: `Bearer ${localStorage.getItem("access") || ""}`,
        },
    }), []);

    const normalizeImageUrl = useCallback((value) => {
        if (!value) return "";

        if (typeof value === "object") {
            value = value.url || value.image || "";
        }

        if (typeof value !== "string") return "";

        if (
            value.startsWith("http://") ||
            value.startsWith("https://") ||
            value.startsWith("data:") ||
            value.startsWith("blob:")
        ) {
            return value;
        }

        try {
            const baseURL = api?.defaults?.baseURL;
            if (baseURL) {
                return new URL(value, baseURL).toString();
            }
        } catch (error) {
            console.warn("Could not resolve student image URL:", error);
        }

        return value;
    }, []);

    const setPreviewImage = useCallback((value) => {
        setImageUrl((previous) => {
            if (
                previous?.startsWith("blob:") &&
                previous !== value
            ) {
                URL.revokeObjectURL(previous);
            }

            return value || "";
        });
    }, []);

    const loadDropdownOptions = useCallback(async () => {
        setOptionError("");

        const requests = [
            {
                label: "academic years",
                url: "/academic-years/",
                setter: setAcademicYears,
            },
            {
                label: "grade classes",
                url: "/api/users/grade-classes/",
                setter: setGrades,
            },
            {
                label: "divisions",
                url: "/api/users/divisions/",
                setter: setDivisions,
            },
            {
                label: "parents",
                url: "/api/super-parents/",
                setter: setSuperParents,
            },
        ];

        const results = await Promise.allSettled(
            requests.map((item) => api.get(item.url, config))
        );

        const failed = [];

        results.forEach((result, index) => {
            const item = requests[index];

            if (result.status === "fulfilled") {
                item.setter(getResults(result.value.data));
            } else {
                console.error(
                    `Failed to load ${item.label}:`,
                    result.reason
                );
                failed.push(item.label);
            }
        });

        if (failed.length) {
            setOptionError(
                `Some options could not be loaded: ${failed.join(", ")}.`
            );
        }
    }, [config]);

    const populateForm = useCallback((data) => {
        form.setFieldsValue({
            full_name: data.full_name ?? "",
            gender: data.gender ?? "",
            academic_year: getRelatedId(data.academic_year),
            grade_class: getRelatedId(data.grade_class),
            division_assigned: getRelatedId(data.division_assigned),
            parent_id: getRelatedId(data.parent_id ?? data.parent),
            previous_class: data.previous_class ?? "",
            major_subject: data.major_subject ?? "",
            previous_school: data.previous_school ?? "",
            hobit: data.hobit ?? "",
            date_of_birth: data.date_of_birth ?? "",
            enrollment_date: data.enrollment_date ?? "",
            semester_status: data.semester_status ?? "",
            student_status: data.student_status ?? "",
            status: data.status ?? "",
            nationality: data.nationality ?? "",
            county_of_origin: data.county_of_origin ?? "",
            father_name: data.father_name ?? "",
            mother_name: data.mother_name ?? "",
            parent_contact: data.parent_contact ?? "",
            health_status: data.health_status ?? "",
            address: data.address ?? "",
        });
    }, [form]);

    const loadAdmissionRecord = useCallback(async () => {
        const currentStudent = studentRef.current;
        const currentId = currentStudent?.id;

        if (!currentId) {
            setRecord(null);
            setLoadError("No student was selected.");
            return;
        }

        const sequence = ++requestSequenceRef.current;

        setLoadingRecord(true);
        setLoadError("");
        setRecord(null);
        setImageFileList([]);
        form.resetFields();
        setPreviewImage("");

        try {
            const response = await api.get(
                `/api/users/students/${currentId}/`,
                config
            );

            if (
                sequence !== requestSequenceRef.current ||
                !visibleRef.current
            ) {
                return;
            }

            const data = {
                ...(studentRef.current || {}),
                ...(response.data || {}),
            };

            setRecord(data);
            populateForm(data);

            setPreviewImage(
                normalizeImageUrl(data.student_admission_image)
            );
        } catch (error) {
            console.error("Failed to load student admission:", error);

            if (
                sequence !== requestSequenceRef.current ||
                !visibleRef.current
            ) {
                return;
            }

            const fallback = studentRef.current || {};

            if (fallback.id) {
                setRecord(fallback);
                populateForm(fallback);

                setPreviewImage(
                    normalizeImageUrl(fallback.student_admission_image)
                );

                setLoadError(
                    "The complete admission record could not be loaded. " +
                    "Some details may be unavailable. Verify the information before saving."
                );
            } else {
                setLoadError(getErrorMessage(error));
            }
        } finally {
            if (sequence === requestSequenceRef.current) {
                setLoadingRecord(false);
            }
        }
    }, [
        config,
        form,
        normalizeImageUrl,
        populateForm,
        setPreviewImage,
    ]);

    useEffect(() => {
        if (!visible || !studentId) return undefined;

        let cancelled = false;

        const initialize = async () => {
            await loadDropdownOptions();

            if (!cancelled && visibleRef.current) {
                await loadAdmissionRecord();
            }
        };

        initialize();

        return () => {
            cancelled = true;
            requestSequenceRef.current += 1;
        };
    }, [
        visible,
        studentId,
        loadDropdownOptions,
        loadAdmissionRecord,
    ]);

    const handleClose = useCallback(() => {
        if (savingRef.current) return;

        requestSequenceRef.current += 1;

        setLoadingRecord(false);
        setLoadError("");
        setOptionError("");
        setRecord(null);
        setImageFileList([]);
        setPreviewImage("");
        form.resetFields();

        if (typeof onClose === "function") {
            onClose();
        }
    }, [form, onClose, setPreviewImage]);

    const handleImageChange = ({ fileList }) => {
        const nextList = (fileList || []).slice(-1);
        setImageFileList(nextList);

        const selectedFile = nextList[0]?.originFileObj;

        if (
            selectedFile &&
            typeof selectedFile === "object" &&
            selectedFile.size !== undefined
        ) {
            const preview = URL.createObjectURL(selectedFile);
            setPreviewImage(preview);
        } else if (!nextList.length) {
            setPreviewImage(
                normalizeImageUrl(record?.student_admission_image)
            );
        }
    };

    const handleImageRemove = () => {
        setImageFileList([]);

        setPreviewImage(
            normalizeImageUrl(record?.student_admission_image)
        );

        return true;
    };

    const handleSubmit = async (values) => {
        const currentId = studentRef.current?.id;

        if (!currentId) {
            message.error("No student was selected for updating.");
            return;
        }

        if (savingRef.current) return;

        savingRef.current = true;
        setLoading(true);

        try {
            const formData = new FormData();

            // Send ordinary fields only. Never append the Upload fileList,
            // existing image URL, or image field as a normal string.
            Object.entries(values).forEach(([key, value]) => {
                if (
                    key === "student_admission_image" ||
                    value === undefined ||
                    value === null ||
                    typeof value === "object"
                ) {
                    return;
                }

                formData.append(key, String(value));
            });

            // Ant Design Upload with beforeUpload={() => false} stores the
            // selected browser File inside originFileObj.
            const uploadItem = imageFileList[0];
            const candidate =
                uploadItem?.originFileObj ||
                (uploadItem instanceof Blob ? uploadItem : null);

            const isRealFile =
                typeof File !== "undefined" &&
                candidate instanceof File;

            if (isRealFile) {
                formData.append(
                    "student_admission_image",
                    candidate,
                    candidate.name
                );
            } else if (candidate instanceof Blob) {
                // Supports Blob uploads if a caller supplied one.
                formData.append(
                    "student_admission_image",
                    candidate,
                    "student-admission-image.jpg"
                );
            }

            // IMPORTANT:
            // Do not manually set Content-Type to application/json.
            // Let Axios/browser generate multipart/form-data with its
            // required boundary for this FormData request.
            const token = localStorage.getItem("access");

            await api.put(
                `/api/users/students/${currentId}/`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token || ""}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            message.success(
                "Student admission updated successfully."
            );

            // Close the popup immediately after the successful PUT.
            requestSequenceRef.current += 1;
            setLoadingRecord(false);
            setLoadError("");
            setOptionError("");
            setRecord(null);
            setImageFileList([]);
            setPreviewImage("");
            form.resetFields();

            if (typeof onClose === "function") {
                onClose();
            }

            // A refresh failure must never prevent the modal from closing.
            if (typeof refresh === "function") {
                Promise.resolve()
                    .then(() => refresh())
                    .catch((refreshError) => {
                        console.error(
                            "Student saved, but table refresh failed:",
                            refreshError
                        );

                        message.warning(
                            "Student saved successfully, but the table could not refresh. Reload the list to see the latest information."
                        );
                    });
            }
        } catch (error) {
            console.error("Student admission update error:", error);
            console.error(
                "Backend response:",
                error?.response?.data
            );

            message.error(
                getErrorMessage(error) ||
                "Unable to update student admission."
            );
        } finally {
            savingRef.current = false;
            setLoading(false);
        }
    };

    const selectedName =
        record?.full_name ||
        student?.full_name ||
        "Student Admission";

    const selectFilter = (inputValue, option) =>
        String(option?.children ?? "")
            .toLowerCase()
            .includes(inputValue.toLowerCase());

    return (
        <Modal
            open={visible}
            title={null}
            onCancel={handleClose}
            footer={null}
            width={900}
            centered
            destroyOnClose
            maskClosable={!loading}
            keyboard={!loading}
            closable={false}
            className="edit-student-admission-modal"
            wrapClassName="esap-modal-wrap"
            styles={{
                body: {
                    padding: 0,
                },
            }}
        >
            <div className="esap-shell">
                <header className="esap-header">
                    <div className="esap-header-icon">
                        <IdcardOutlined />
                    </div>

                    <div className="esap-header-copy">
                        <div className="esap-eyebrow">
                            STUDENT RECORDS MANAGEMENT
                        </div>

                        <div className="esap-title">
                            Edit Student Admission
                        </div>

                        <div className="esap-subtitle">
                            Update personal details, academic placement,
                            contact information, and student photograph.
                        </div>
                    </div>

                    <Button
                        type="text"
                        className="esap-close-button"
                        icon={<CloseOutlined />}
                        onClick={handleClose}
                        disabled={loading}
                        aria-label="Close edit admission"
                    />
                </header>

                <div className="esap-student-strip">
                    <Avatar
                        size={54}
                        src={imageUrl || undefined}
                        icon={<UserOutlined />}
                        className="esap-avatar"
                    />

                    <div className="esap-student-copy">
                        <div className="esap-student-name">
                            {selectedName}
                        </div>

                        <div className="esap-student-meta">
                            {record?.student_id ||
                                record?.admission_number ||
                                `Admission record #${studentId || "—"}`}
                        </div>
                    </div>

                    <Tag color="blue">
                        {record?.status ||
                            student?.status ||
                            "Admission Record"}
                    </Tag>
                </div>

                <div className="esap-body">
                    {loadError && (
                        <Alert
                            type="warning"
                            showIcon
                            message="Record loading notice"
                            description={loadError}
                            closable
                            onClose={() => setLoadError("")}
                            className="esap-alert"
                        />
                    )}

                    {optionError && (
                        <Alert
                            type="warning"
                            showIcon
                            message="Some dropdown options are unavailable"
                            description={optionError}
                            action={
                                <Button
                                    size="small"
                                    icon={<ReloadOutlined />}
                                    onClick={loadDropdownOptions}
                                >
                                    Retry
                                </Button>
                            }
                            className="esap-alert"
                        />
                    )}

                    <Spin
                        spinning={loadingRecord}
                        tip="Loading student admission..."
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSubmit}
                            preserve={false}
                            className="esap-form"
                        >
                            <section className="esap-section">
                                <div className="esap-section-heading">
                                    <span className="esap-section-icon">
                                        <BookOutlined />
                                    </span>

                                    <div>
                                        <div className="esap-section-title">
                                            Academic Information
                                        </div>

                                        <div className="esap-section-description">
                                            Manage academic year, grade class,
                                            division, and parent.
                                        </div>
                                    </div>
                                </div>

                                <Row gutter={[16, 0]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Academic Year"
                                            name="academic_year"
                                        >
                                            <Select
                                                showSearch
                                                allowClear
                                                placeholder="Select academic year"
                                                optionFilterProp="children"
                                                filterOption={selectFilter}
                                            >
                                                {academicYears.map((year) => (
                                                    <Option
                                                        key={year.id}
                                                        value={year.id}
                                                    >
                                                        {year.name ||
                                                            year.academic_year ||
                                                            `Academic Year ${year.id}`}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Grade Class"
                                            name="grade_class"
                                        >
                                            <Select
                                                showSearch
                                                allowClear
                                                placeholder="Select grade class"
                                                optionFilterProp="children"
                                                filterOption={selectFilter}
                                            >
                                                {grades.map((item) => (
                                                    <Option
                                                        key={item.id}
                                                        value={item.id}
                                                    >
                                                        {item.name ||
                                                            item.grade_name ||
                                                            item.class_name ||
                                                            `Grade Class ${item.id}`}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Division"
                                            name="division_assigned"
                                        >
                                            <Select
                                                showSearch
                                                allowClear
                                                placeholder="Select division"
                                                optionFilterProp="children"
                                                filterOption={selectFilter}
                                            >
                                                {divisions.map((item) => (
                                                    <Option
                                                        key={item.id}
                                                        value={item.id}
                                                    >
                                                        {item.name ||
                                                            item.division_name ||
                                                            `Division ${item.id}`}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Parent"
                                            name="parent_id"
                                        >
                                            <Select
                                                showSearch
                                                allowClear
                                                placeholder="Select parent"
                                                optionFilterProp="children"
                                                filterOption={selectFilter}
                                            >
                                                {superParents.map((parent) => (
                                                    <Option
                                                        key={parent.id}
                                                        value={parent.id}
                                                    >
                                                        {parent.full_name ||
                                                            parent.username ||
                                                            parent.email ||
                                                            `Parent ${parent.id}`}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </section>

                            <Divider className="esap-divider" />

                            <section className="esap-section">
                                <div className="esap-section-heading">
                                    <span className="esap-section-icon esap-icon-blue">
                                        <UserOutlined />
                                    </span>

                                    <div>
                                        <div className="esap-section-title">
                                            Personal Information
                                        </div>

                                        <div className="esap-section-description">
                                            Confirm the student's identity and
                                            personal details.
                                        </div>
                                    </div>
                                </div>

                                <Row gutter={[16, 0]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Full Name"
                                            name="full_name"
                                        >
                                            <Input
                                                placeholder="Enter student's full name"
                                                maxLength={150}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Gender"
                                            name="gender"
                                        >
                                            <Select
                                                allowClear
                                                placeholder="Select gender"
                                            >
                                                <Option value="Male">Male</Option>
                                                <Option value="Female">Female</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Date of Birth"
                                            name="date_of_birth"
                                        >
                                            <Input type="date" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Nationality"
                                            name="nationality"
                                        >
                                            <Input placeholder="Enter nationality" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="County of Origin"
                                            name="county_of_origin"
                                        >
                                            <Input placeholder="Enter county of origin" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Previous Class"
                                            name="previous_class"
                                        >
                                            <Input placeholder="Enter previous class" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Previous School"
                                            name="previous_school"
                                        >
                                            <Input placeholder="Enter previous school" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Major Subject"
                                            name="major_subject"
                                        >
                                            <Input placeholder="Enter major subject" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Hobby"
                                            name="hobit"
                                        >
                                            <Input placeholder="Enter hobby" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </section>

                            <Divider className="esap-divider" />

                            <section className="esap-section">
                                <div className="esap-section-heading">
                                    <span className="esap-section-icon esap-icon-gold">
                                        <TeamOutlined />
                                    </span>

                                    <div>
                                        <div className="esap-section-title">
                                            Parent and Guardian Information
                                        </div>

                                        <div className="esap-section-description">
                                            Maintain family contact and guardian
                                            details.
                                        </div>
                                    </div>
                                </div>

                                <Row gutter={[16, 0]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Father's Name"
                                            name="father_name"
                                        >
                                            <Input placeholder="Enter father's name" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Mother's Name"
                                            name="mother_name"
                                        >
                                            <Input placeholder="Enter mother's name" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Parent Contact"
                                            name="parent_contact"
                                        >
                                            <Input
                                                placeholder="Enter parent phone number"
                                                maxLength={50}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24}>
                                        <Form.Item
                                            label="Address"
                                            name="address"
                                        >
                                            <Input.TextArea
                                                rows={3}
                                                placeholder="Enter residential address"
                                                maxLength={1000}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </section>

                            <Divider className="esap-divider" />

                            <section className="esap-section">
                                <div className="esap-section-heading">
                                    <span className="esap-section-icon esap-icon-health">
                                        <HeartOutlined />
                                    </span>

                                    <div>
                                        <div className="esap-section-title">
                                            Enrollment and Student Status
                                        </div>

                                        <div className="esap-section-description">
                                            Update admission date, semester,
                                            and enrollment status.
                                        </div>
                                    </div>
                                </div>

                                <Row gutter={[16, 0]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Enrollment Date"
                                            name="enrollment_date"
                                        >
                                            <Input type="date" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Semester Status"
                                            name="semester_status"
                                        >
                                            <Select
                                                allowClear
                                                placeholder="Select semester"
                                            >
                                                <Option value="First">
                                                    First Semester
                                                </Option>
                                                <Option value="Second">
                                                    Second Semester
                                                </Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Student Status"
                                            name="student_status"
                                        >
                                            <Select
                                                allowClear
                                                placeholder="Select student type"
                                            >
                                                <Option value="Regular">
                                                    Regular
                                                </Option>
                                                <Option value="Dependent">
                                                    Dependent
                                                </Option>
                                                <Option value="Scholarship Student">
                                                    Scholarship Student
                                                </Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Admission Status"
                                            name="status"
                                        >
                                            <Select
                                                allowClear
                                                placeholder="Select admission status"
                                            >
                                                <Option value="Pending">
                                                    Pending
                                                </Option>
                                                <Option value="Enrolled">
                                                    Enrolled
                                                </Option>
                                                <Option value="Graduated">
                                                    Graduated
                                                </Option>
                                                <Option value="Dropped">
                                                    Dropped
                                                </Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24}>
                                        <Form.Item
                                            label="Health Status"
                                            name="health_status"
                                        >
                                            <Input.TextArea
                                                rows={2}
                                                placeholder="Enter relevant health information"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </section>

                            <Divider className="esap-divider" />

                            <section className="esap-section">
                                <div className="esap-section-heading">
                                    <span className="esap-section-icon">
                                        <CameraOutlined />
                                    </span>

                                    <div>
                                        <div className="esap-section-title">
                                            Student Photograph
                                        </div>

                                        <div className="esap-section-description">
                                            Keep the existing photo or select
                                            a replacement image.
                                        </div>
                                    </div>
                                </div>

                                <div className="esap-image-panel">
                                    <div className="esap-image-preview">
                                        {imageUrl ? (
                                            <img
                                                src={imageUrl}
                                                alt="Student admission"
                                            />
                                        ) : (
                                            <Avatar
                                                size={90}
                                                icon={<UserOutlined />}
                                            />
                                        )}
                                    </div>

                                    <div className="esap-image-details">
                                        <div className="esap-image-title">
                                            Student Admission Image
                                        </div>

                                        <div className="esap-image-hint">
                                            Select a new image to replace the
                                            existing photograph. If no new image
                                            is selected, the existing image is
                                            left unchanged.
                                        </div>

                                        <Upload
                                            accept="image/*"
                                            maxCount={1}
                                            fileList={imageFileList}
                                            beforeUpload={() => false}
                                            onChange={handleImageChange}
                                            onRemove={handleImageRemove}
                                        >
                                            <Button
                                                icon={<UploadOutlined />}
                                                className="esap-upload-button"
                                            >
                                                Choose New Image
                                            </Button>
                                        </Upload>
                                    </div>
                                </div>
                            </section>
                        </Form>
                    </Spin>
                </div>

                <footer className="esap-footer">
                    <div className="esap-footer-note">
                        <CalendarOutlined />
                        <span>
                            Review all changes before updating this record.
                        </span>
                    </div>

                    <Space>
                        <Button
                            icon={<CloseOutlined />}
                            onClick={handleClose}
                            disabled={loading}
                            className="esap-cancel-button"
                        >
                            Cancel
                        </Button>

                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={loading}
                            disabled={loadingRecord || !studentId}
                            onClick={() => form.submit()}
                            className="esap-save-button"
                        >
                            Update Admission
                        </Button>
                    </Space>
                </footer>
            </div>

            <style>{`
                .esap-modal-wrap {
                    padding: 20px !important;
                    overflow-y: auto !important;
                    box-sizing: border-box !important;
                }

                .edit-student-admission-modal {
                    max-width: calc(100vw - 24px);
                }

                .edit-student-admission-modal .ant-modal-content {
                    padding: 0 !important;
                    overflow: hidden;
                    border-radius: 19px !important;
                    box-shadow: 0 24px 80px rgba(5, 25, 45, .25);
                }

                .esap-shell {
                    display: flex;
                    flex-direction: column;
                    max-height: min(88vh, 900px);
                    color: #142536;
                    background: #fff;
                    overflow: hidden;
                    border-radius: 19px;
                }

                .esap-header {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    padding: 22px 24px;
                    color: #fff;
                    background: linear-gradient(115deg, ${NAVY}, ${BLUE});
                    flex-shrink: 0;
                }

                .esap-header-icon {
                    width: 48px;
                    height: 48px;
                    flex: 0 0 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 14px;
                    color: ${NAVY};
                    background: linear-gradient(135deg, ${GOLD}, #EFA81C);
                    font-size: 22px;
                }

                .esap-header-copy {
                    min-width: 0;
                    flex: 1;
                }

                .esap-eyebrow {
                    margin-bottom: 5px;
                    color: ${GOLD};
                    font-size: 10px;
                    font-weight: 850;
                    letter-spacing: 1.5px;
                }

                .esap-title {
                    font-size: 21px;
                    line-height: 1.25;
                    font-weight: 850;
                    color: #fff;
                }

                .esap-subtitle {
                    margin-top: 6px;
                    color: rgba(255,255,255,.78);
                    font-size: 12px;
                    line-height: 1.6;
                }

                .esap-close-button {
                    color: #fff !important;
                    font-size: 16px;
                    flex: 0 0 auto;
                }

                .esap-close-button:hover {
                    color: ${GOLD} !important;
                    background: rgba(255,255,255,.10) !important;
                }

                .esap-student-strip {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                    padding: 15px 24px;
                    border-bottom: 1px solid #e9eef2;
                    background: #f8fafc;
                    flex-shrink: 0;
                }

                .esap-avatar {
                    border: 2px solid #fff;
                    box-shadow: 0 0 0 1px #dce5ec;
                    background: ${BLUE};
                }

                .esap-student-copy {
                    min-width: 0;
                    flex: 1;
                }

                .esap-student-name {
                    overflow-wrap: anywhere;
                    color: ${NAVY};
                    font-size: 15px;
                    font-weight: 850;
                }

                .esap-student-meta {
                    margin-top: 4px;
                    color: #64748b;
                    font-size: 11px;
                }

                .esap-body {
                    flex: 1;
                    min-height: 0;
                    overflow-y: auto;
                    overscroll-behavior: contain;
                    padding: 22px 24px 8px;
                }

                .esap-body::-webkit-scrollbar {
                    width: 9px;
                }

                .esap-body::-webkit-scrollbar-track {
                    background: #f1f4f7;
                    border-radius: 10px;
                }

                .esap-body::-webkit-scrollbar-thumb {
                    background: #b9c7d3;
                    border-radius: 10px;
                    border: 2px solid #f1f4f7;
                }

                .esap-body::-webkit-scrollbar-thumb:hover {
                    background: #8096a8;
                }

                .esap-alert {
                    margin-bottom: 16px;
                    border-radius: 10px;
                }

                .esap-section {
                    margin-bottom: 4px;
                }

                .esap-section-heading {
                    display: flex;
                    align-items: center;
                    gap: 11px;
                    margin-bottom: 18px;
                }

                .esap-section-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 36px;
                    height: 36px;
                    flex: 0 0 36px;
                    border-radius: 11px;
                    color: ${NAVY};
                    background: #fff2c7;
                    font-size: 16px;
                }

                .esap-icon-blue {
                    color: #0877A8;
                    background: #e5f5fc;
                }

                .esap-icon-gold {
                    color: #946200;
                    background: #fff3d2;
                }

                .esap-icon-health {
                    color: #27814d;
                    background: #e5f6eb;
                }

                .esap-section-title {
                    color: ${NAVY};
                    font-size: 13px;
                    font-weight: 850;
                }

                .esap-section-description {
                    margin-top: 3px;
                    color: #778493;
                    font-size: 11px;
                    line-height: 1.5;
                }

                .esap-form .ant-form-item {
                    margin-bottom: 16px;
                }

                .esap-form .ant-form-item-label {
                    padding-bottom: 5px;
                }

                .esap-form .ant-form-item-label > label {
                    height: auto;
                    color: #344659;
                    font-size: 11px;
                    font-weight: 750;
                }

                .esap-form .ant-input,
                .esap-form .ant-input-affix-wrapper,
                .esap-form .ant-select-selector {
                    min-height: 39px;
                    border-color: #dce4ea !important;
                    border-radius: 9px !important;
                    box-shadow: none !important;
                    font-size: 12px;
                }

                .esap-form .ant-input {
                    padding: 8px 11px;
                }

                .esap-form .ant-input:hover,
                .esap-form .ant-input-affix-wrapper:hover,
                .esap-form .ant-select-selector:hover {
                    border-color: #8fa8bc !important;
                }

                .esap-form .ant-input:focus,
                .esap-form .ant-input-affix-wrapper-focused,
                .esap-form .ant-select-focused .ant-select-selector {
                    border-color: ${BLUE} !important;
                    box-shadow: 0 0 0 2px rgba(6,58,107,.08) !important;
                }

                .esap-form .ant-select-selection-item,
                .esap-form .ant-select-selection-placeholder {
                    line-height: 37px !important;
                    font-size: 12px;
                }

                .esap-form textarea.ant-input {
                    min-height: auto;
                    resize: vertical;
                }

                .esap-divider {
                    margin: 14px 0 22px !important;
                    border-color: #e8edf1 !important;
                }

                .esap-image-panel {
                    display: flex;
                    align-items: center;
                    gap: 18px;
                    padding: 17px;
                    border: 1px solid #e2e9ee;
                    border-radius: 13px;
                    background: #f9fbfc;
                    margin-bottom: 20px;
                }

                .esap-image-preview {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 108px;
                    height: 108px;
                    flex: 0 0 108px;
                    overflow: hidden;
                    border: 1px solid #e0e7ed;
                    border-radius: 13px;
                    background: #eef3f7;
                }

                .esap-image-preview img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .esap-image-details {
                    min-width: 0;
                    flex: 1;
                }

                .esap-image-title {
                    color: ${NAVY};
                    font-size: 13px;
                    font-weight: 850;
                }

                .esap-image-hint {
                    margin: 6px 0 12px;
                    color: #6b7785;
                    font-size: 11px;
                    line-height: 1.6;
                }

                .esap-upload-button {
                    border-radius: 8px;
                    border-color: #ccd8e1;
                    color: ${BLUE};
                    font-size: 11px;
                    font-weight: 750;
                }

                .esap-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 16px 24px;
                    border-top: 1px solid #e8edf1;
                    background: #fff;
                    flex-shrink: 0;
                }

                .esap-footer-note {
                    display: flex;
                    align-items: center;
                    gap: 7px;
                    color: #718092;
                    font-size: 10px;
                    line-height: 1.5;
                }

                .esap-footer-note .anticon {
                    color: #C0750D;
                }

                .esap-cancel-button,
                .esap-save-button {
                    height: 39px;
                    border-radius: 9px;
                    padding: 0 16px;
                    font-size: 11px;
                    font-weight: 800;
                }

                .esap-cancel-button {
                    border-color: #dce4ea;
                    color: #445467;
                }

                .esap-save-button {
                    background: ${NAVY};
                    border-color: ${NAVY};
                    box-shadow: 0 4px 12px rgba(6,42,75,.14);
                }

                .esap-save-button:hover {
                    background: ${BLUE} !important;
                    border-color: ${BLUE} !important;
                }

                @media (max-width: 600px) {
                    .esap-modal-wrap {
                        padding: 8px !important;
                    }

                    .esap-shell {
                        max-height: 94vh;
                    }

                    .esap-header {
                        padding: 17px 15px;
                        gap: 10px;
                    }

                    .esap-header-icon {
                        width: 40px;
                        height: 40px;
                        flex-basis: 40px;
                        font-size: 18px;
                    }

                    .esap-title {
                        font-size: 17px;
                    }

                    .esap-subtitle {
                        font-size: 11px;
                    }

                    .esap-student-strip {
                        padding: 12px 15px;
                    }

                    .esap-body {
                        padding: 17px 15px 5px;
                    }

                    .esap-footer {
                        align-items: stretch;
                        flex-direction: column;
                        padding: 13px 15px;
                    }

                    .esap-footer > .ant-space {
                        display: flex;
                        width: 100%;
                    }

                    .esap-footer > .ant-space .ant-btn {
                        flex: 1;
                    }

                    .esap-image-panel {
                        align-items: flex-start;
                        flex-direction: column;
                    }
                }
            `}</style>
        </Modal>
    );
};

export default EditStudentAdmissionPopup;
